/**
 * Blixo E2E Encryption
 *
 * Flow:
 * 1. On first login, generate RSA-2048 key pair in browser
 * 2. Store private key in localStorage (never sent to server)
 * 3. Send public key to server → stored on User model
 * 4. When sending message:
 *    - Generate random AES-256-GCM key
 *    - Encrypt message with AES key
 *    - Encrypt AES key with BOTH user's and admin's RSA public keys
 *    - Send all ciphertext to server
 * 5. Server stores ciphertext only — cannot read messages
 * 6. On receiving, decrypt AES key with own private key, then decrypt message
 */

const PRIVATE_KEY_STORAGE = 'blixo_e2e_private_key';
const PUBLIC_KEY_STORAGE  = 'blixo_e2e_public_key';

// ── Key Generation ────────────────────────────────────────────────
export const generateKeyPair = async () => {
  const keyPair = await window.crypto.subtle.generateKey(
    { name: 'RSA-OAEP', modulusLength: 2048, publicExponent: new Uint8Array([1, 0, 1]), hash: 'SHA-256' },
    true,
    ['encrypt', 'decrypt']
  );

  const publicKeyExported  = await window.crypto.subtle.exportKey('spki',  keyPair.publicKey);
  const privateKeyExported = await window.crypto.subtle.exportKey('pkcs8', keyPair.privateKey);

  const publicKeyPem  = arrayBufferToPem(publicKeyExported,  'PUBLIC KEY');
  const privateKeyPem = arrayBufferToPem(privateKeyExported, 'PRIVATE KEY');

  localStorage.setItem(PRIVATE_KEY_STORAGE, privateKeyPem);
  localStorage.setItem(PUBLIC_KEY_STORAGE,  publicKeyPem);

  return { publicKeyPem };
};

export const hasKeyPair      = () => !!localStorage.getItem(PRIVATE_KEY_STORAGE);
export const getLocalPublicKey  = () => localStorage.getItem(PUBLIC_KEY_STORAGE);
export const getLocalPrivateKey = () => localStorage.getItem(PRIVATE_KEY_STORAGE);

export const clearKeyPair = () => {
  localStorage.removeItem(PRIVATE_KEY_STORAGE);
  localStorage.removeItem(PUBLIC_KEY_STORAGE);
};

// ── Encrypt message for two recipients (user + admin) ────────────
// Returns: { encryptedContent, iv, encryptedAesKeyForUser, encryptedAesKeyForAdmin }
export const encryptMessage = async (plaintext, userPublicKeyPem, adminPublicKeyPem) => {
  try {
    // 1. Generate random AES-256-GCM key
    const aesKey = await window.crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']
    );

    // 2. Encrypt message with AES
    const iv       = window.crypto.getRandomValues(new Uint8Array(12));
    const encoded  = new TextEncoder().encode(plaintext);
    const ciphertext = await window.crypto.subtle.encrypt({ name: 'AES-GCM', iv }, aesKey, encoded);

    // 3. Export raw AES key
    const rawAesKey = await window.crypto.subtle.exportKey('raw', aesKey);

    // 4. Encrypt AES key with user's RSA public key
    const userPubKey           = await importPublicKey(userPublicKeyPem);
    const encryptedAesKeyForUser = await window.crypto.subtle.encrypt({ name: 'RSA-OAEP' }, userPubKey, rawAesKey);

    // 5. Encrypt AES key with admin's RSA public key
    const adminPubKey            = await importPublicKey(adminPublicKeyPem);
    const encryptedAesKeyForAdmin = await window.crypto.subtle.encrypt({ name: 'RSA-OAEP' }, adminPubKey, rawAesKey);

    return {
      encryptedContent:       arrayBufferToBase64(ciphertext),
      iv:                     arrayBufferToBase64(iv),
      encryptedAesKeyForUser:  arrayBufferToBase64(encryptedAesKeyForUser),
      encryptedAesKeyForAdmin: arrayBufferToBase64(encryptedAesKeyForAdmin),
    };
  } catch (err) {
    console.error('Encryption failed:', err);
    return null;
  }
};

// ── Decrypt message using own private key ────────────────────────
export const decryptMessage = async (encryptedContent, iv, encryptedAesKey) => {
  try {
    const privateKeyPem = getLocalPrivateKey();
    if (!privateKeyPem) return '[🔒 No private key — cannot decrypt]';

    const privateKey = await importPrivateKey(privateKeyPem);

    // 1. Decrypt AES key
    const rawAesKey = await window.crypto.subtle.decrypt(
      { name: 'RSA-OAEP' },
      privateKey,
      base64ToArrayBuffer(encryptedAesKey)
    );

    // 2. Import AES key
    const aesKey = await window.crypto.subtle.importKey(
      'raw', rawAesKey, { name: 'AES-GCM' }, false, ['decrypt']
    );

    // 3. Decrypt message
    const plainBuffer = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: base64ToArrayBuffer(iv) },
      aesKey,
      base64ToArrayBuffer(encryptedContent)
    );

    return new TextDecoder().decode(plainBuffer);
  } catch (err) {
    console.error('Decryption failed:', err);
    return '[🔒 Encrypted message]';
  }
};

// ── Helpers ───────────────────────────────────────────────────────
const importPublicKey = (pem) => {
  const binaryDer = pemToArrayBuffer(pem, 'PUBLIC KEY');
  return window.crypto.subtle.importKey(
    'spki', binaryDer, { name: 'RSA-OAEP', hash: 'SHA-256' }, false, ['encrypt']
  );
};

const importPrivateKey = (pem) => {
  const binaryDer = pemToArrayBuffer(pem, 'PRIVATE KEY');
  return window.crypto.subtle.importKey(
    'pkcs8', binaryDer, { name: 'RSA-OAEP', hash: 'SHA-256' }, false, ['decrypt']
  );
};

const pemToArrayBuffer = (pem, label) => {
  const base64 = pem
    .replace(`-----BEGIN ${label}-----`, '')
    .replace(`-----END ${label}-----`, '')
    .replace(/\s/g, '');
  return base64ToArrayBuffer(base64);
};

const arrayBufferToPem = (buffer, label) => {
  const base64 = arrayBufferToBase64(buffer);
  const lines  = base64.match(/.{1,64}/g).join('\n');
  return `-----BEGIN ${label}-----\n${lines}\n-----END ${label}-----`;
};

const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary  = '';
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
};

const base64ToArrayBuffer = (base64) => {
  const binary = atob(base64);
  const bytes  = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
};
